// AdvancedFiltersAndColumns.jsx
import React, { useState } from 'react';
import { Button, Collapse, Form, Row, Col, Tab, Tabs } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faColumns, faChevronDown, faChevronUp, faSearch, faPlus } from '@fortawesome/free-solid-svg-icons';
import CheckboxDropdown from '../../../../../components/CheckboxDropdown/CheckboxDropdown';
import DateRangePicker from '../../../../../components/DateRangePicker/DateRangePicker';
import MultiAutocompleteSelect from '../../../../../components/Multiautoselecttags/Multiautocompleteselecttags';

const AdvancedFiltersAndColumns = ({
  filters,
  onFilterChange,
  onApplyFilters,
  activeFilterCount,
  availableColumns,
  visibleColumns,
  onColumnToggle,
  onSelectAllColumns,
  onClearAllColumns,
  onResetAll,
  kindergartenOptions = [],
  districtOptions = [],
  benefitsOptions = [],
  disabilitiesOptions = [],
  onKindergartenSearch,
  onKindergartenLoadMore,
  kindergartenLoading,
  kindergartenHasMore,
  selectedKindergartensList = [],
  onAddKindergarten,
  onRemoveKindergarten,
  kgById,
  inviteKindergartenOptions = [],
  inviteKindergartenLoading = false,
  inviteKindergartenHasMore = false,
  onInviteKindergartenSearch,
  onInviteKindergartenLoadMore,
  inviteKgById = {}
}) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('filters');
  const [tempSelectedKg, setTempSelectedKg] = useState('');

  // Возрастные диапазоны
  const ageOptions = [
    { value: '0-1', label: '0-1 рік' },
    { value: '1-2', label: '1-2 роки' },
    { value: '2-3', label: '2-3 роки' },
    { value: '3-4', label: '3-4 роки' },
    { value: '4-5', label: '4-5 років' },
    { value: '5-6', label: '5-6 років' },
    { value: '6-7', label: '6-7 років' },
    { value: '7-8', label: '7-8 років' },
    { value: '8+', label: 'Старше 8 років' }
  ];


  const handleAddKindergarten = () => {
    if (tempSelectedKg) {
      onAddKindergarten(tempSelectedKg);
      setTempSelectedKg('');
    }
  };


  const handleResetAll = () => {
    onResetAll?.();
    onFilterChange('reset', null);
  };

  const handleApplyAndClose = () => {
    onApplyFilters();
    setOpen(false);
  };

  // Формируем массив выбранных фильтров для отображения в тегах
  const getSelectedFiltersList = () => {
    const selected = [];

    if (filters.ageRanges?.length) {
      selected.push({ type: 'Вік', count: filters.ageRanges.length });
    }
    
    if (filters.districts?.length) {
      selected.push({ type: 'Райони', count: filters.districts.length, values: filters.districts });
    }
    if (filters.genders?.length) {
      const genderLabels = filters.genders.map(g => g === 'boy' ? 'Хлопчики' : 'Дівчатка');
      selected.push({ type: 'Стать', count: filters.genders.length, values: genderLabels });
    }
    if (filters.benefitTypes?.length) {
      selected.push({ type: 'Пільги', count: filters.benefitTypes.length, values: filters.benefitTypes });
    }
    if (filters.dissabilityTypes?.length) {
      selected.push({ type: 'ІРЦ', count: filters.dissabilityTypes.length, values: filters.dissabilityTypes });
    }
    if (filters.placeTypes?.length) {
      selected.push({ type: 'Тип місця', count: filters.placeTypes.length, values: filters.placeTypes });
    }
    if (filters.statuses?.length) {
      selected.push({ type: 'Статуси', count: filters.statuses.length, values: filters.statuses });
    }
    if (filters.selectedKindergartens?.length) {
      selected.push({ type: 'Садочки', count: filters.selectedKindergartens.length, values: filters.selectedKindergartens });
    }
    if (filters.ageFrom || filters.ageTo) {
      const ageText = `${filters.ageFrom || '0'} - ${filters.ageTo || '∞'} р.`;
      selected.push({ type: 'Вік', count: 1, values: [ageText] });
    }
    if (filters.createdFrom || filters.createdTo) {
      selected.push({ type: 'Дата створення', count: 1, values: ['обрано'] });
    }
    if (filters.preferDateFrom || filters.preferDateTo) {
      selected.push({ type: 'Бажана дата', count: 1, values: ['обрано'] });
    }
    if (filters.hasInvite) {
      selected.push({ type: 'Запрошення', count: 1, values: ['було'] });
    }
    if (filters.hasDeclinedInvite) {
      selected.push({ type: 'Відмови', count: 1, values: ['були'] });
    }
    if (filters.onlyApprovedBenefits) {
      selected.push({ type: 'Пільги', count: 1, values: ['лише підтверджені'] });
    }
    if (filters.hasSelectedKindergartens) {
      selected.push({ type: 'Вибрані ЗДО', count: 1, values: ['наявні'] });
    }
    
    return selected;
  };

  const selectedFilters = getSelectedFiltersList();

  return (
    <div className="advanced-filters mb-3">
      {/* Верхняя панель: фильтры + поиск */}
      <div className="filters-top-bar d-flex align-items-center justify-content-between gap-3">
        <div className="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
          <Button
            variant="outline-secondary"
            onClick={() => setOpen(!open)}
            className="d-flex align-items-center gap-2 flex-shrink-0"
            size="sm"
          >
            <FontAwesomeIcon icon={faFilter} />
            Фільтри
            {activeFilterCount > 0 && (
              <span className="badge bg-primary ms-1">{activeFilterCount}</span>
            )}
            <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="ms-1" />
          </Button>

          {/* Теги выбранных фильтров (видны только когда панель закрыта) */}
          {!open && selectedFilters.length > 0 && (
            <div className="selected-filters-tags d-flex flex-wrap gap-1 align-items-center">
              {selectedFilters.slice(0, 4).map((filter, idx) => (
                <span key={idx} className="selected-filter-tag" title={filter.values?.join(', ')}>
                  {filter.type}: {filter.count}
                </span>
              ))}
              {selectedFilters.length > 4 && (
                <span className="selected-filter-tag more-tag">
                  +{selectedFilters.length - 4}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Строка поиска - видна только когда панель фильтров закрыта */}
        {!open && (
          <div className="search-box flex-shrink-0">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <Form.Control
              type="text"
              placeholder="Пошук заявок..."
              className="applications-search"
              onChange={(e) => onFilterChange('globalSearch', e.target.value)}
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Панель фильтров (раскрывается) */}
      <Collapse in={open}>
        <div className="filters-panel mt-3 p-3 border rounded bg-light">
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
            {/* Вкладка ФИЛЬТРЫ */}
            <Tab 
              eventKey="filters" 
              title={<span><FontAwesomeIcon icon={faFilter} className="me-1" /> Фільтри</span>}
            >
              <Row>
                {/* Дитина */}
                <Col md={4} lg={3}>
                  <h6>Дитина</h6>
                  <CheckboxDropdown
                    isShowInputOption={false}
                    label="Вік"
                    options={ageOptions}
                    selectedValues={filters.ageRanges || []}
                    onToggle={(val) => {
                      const newVal = filters.ageRanges?.includes(val)
                        ? filters.ageRanges.filter(v => v !== val)
                        : [...(filters.ageRanges || []), val];
                      onFilterChange('ageRanges', newVal);
                    }}
                    onSelectAll={() => onFilterChange('ageRanges', ageOptions.map(opt => opt.value))}
                    onClearAll={() => onFilterChange('ageRanges', [])}
                    showTags={true}
                    placeholder="Вік дитини"
                  />

                  <CheckboxDropdown
                    isShowInputOption={false}
                    label="Стать"
                    options={[
                      { value: 'boy', label: 'Хлопчик' },
                      { value: 'girl', label: 'Дівчинка' },
                    ]}
                    selectedValues={filters.genders || []}
                    onToggle={(val) => {
                      const newVal = filters.genders?.includes(val)
                        ? filters.genders.filter(v => v !== val)
                        : [...(filters.genders || []), val];
                      onFilterChange('genders', newVal);
                    }}
                    onSelectAll={() => onFilterChange('genders', ['boy', 'girl'])}
                    onClearAll={() => onFilterChange('genders', [])}
                    showTags={true}
                  />
                </Col>

                {/* Пільги */}
                <Col md={4} lg={3}>
                  <h6>Пільги</h6>
                  <CheckboxDropdown
                    isShowInputOption={false}
                    label="Пільги"
                    options={benefitsOptions.slice(0, 10)}
                    selectedValues={filters.benefitTypes || []}
                    onToggle={(val) => {
                      const newVal = filters.benefitTypes?.includes(val)
                        ? filters.benefitTypes.filter(v => v !== val)
                        : [...(filters.benefitTypes || []), val];
                      onFilterChange('benefitTypes', newVal);
                    }}
                    onSelectAll={() => onFilterChange('benefitTypes', benefitsOptions.map(opt => opt.value))}
                    onClearAll={() => onFilterChange('benefitTypes', [])}
                    showTags={true}
                    placeholder="Типи пільг"
                  />
                  <Form.Check
                    type="switch"
                    label="Тільки підтверджені"
                    checked={filters.onlyApprovedBenefits || false}
                    onChange={(e) => onFilterChange('onlyApprovedBenefits', e.target.checked)}
                    className="mt-1"
                    size="sm"
                  />
                </Col>

                {/* Труднощі (ІРЦ) */}
                <Col md={4} lg={3}>
                  <h6>Труднощі (ІРЦ)</h6>
                  <CheckboxDropdown
                    isShowInputOption={false}
                    label="Труднощі (ІРЦ)"
                    options={disabilitiesOptions}
                    selectedValues={filters.dissabilityTypes || []}
                    onToggle={(val) => {
                      const newVal = filters.dissabilityTypes?.includes(val)
                        ? filters.dissabilityTypes.filter(v => v !== val)
                        : [...(filters.dissabilityTypes || []), val];
                      onFilterChange('dissabilityTypes', newVal);
                    }}
                    onSelectAll={() => onFilterChange('dissabilityTypes', disabilitiesOptions.map(opt => opt.value))}
                    onClearAll={() => onFilterChange('dissabilityTypes', [])}
                    showTags={true}
                    placeholder="Типи труднощів"
                  />
                </Col>

                {/* Тип місця */}
                <Col md={4} lg={3}>
                  <h6>Тип місця</h6>
                  <CheckboxDropdown
                    isShowInputOption={false}
                    label="Типи місць"
                    options={[
                      { value: 'withoutBed', label: 'Короткотривале' },
                      { value: 'inclusive', label: 'Інклюзія' },
                      { value: 'fullDay', label: 'Повний день' },
                    ]}
                    selectedValues={filters.placeTypes || []}
                    onToggle={(val) => {
                      const newVal = filters.placeTypes?.includes(val)
                        ? filters.placeTypes.filter(v => v !== val)
                        : [...(filters.placeTypes || []), val];
                      onFilterChange('placeTypes', newVal);
                    }}
                    onSelectAll={() => onFilterChange('placeTypes', ['withoutBed', 'inclusive', 'fullDay'])}
                    onClearAll={() => onFilterChange('placeTypes', [])}
                    showTags={true}
                  />
                </Col>

                {/* Даты */}
                <Col md={6} lg={4}>
                  <h6>Дата створення заявки</h6>
                  <DateRangePicker
                    start={filters.createdFrom}
                    end={filters.createdTo}
                    onChange={(start, end) => {
                      onFilterChange('createdFrom', start);
                      onFilterChange('createdTo', end);
                    }}
                  />
                </Col>

                <Col md={6} lg={4}>
                  <h6>Бажана дата вступу</h6>
                  <DateRangePicker
                    start={filters.preferDateFrom}
                    end={filters.preferDateTo}
                    onChange={(start, end) => {
                      onFilterChange('preferDateFrom', start);
                      onFilterChange('preferDateTo', end);
                    }}
                  />
                </Col>

                {/* Запрошення */}
                <Col md={12} lg={12}>
                  <h6>Актуальне запрошення</h6>
                  <Row>
                    {/* Статус запрошення */}
                    <Col md={4}>
                      
                      <CheckboxDropdown
                        label="Статус запрошення"
                        options={[
                          { value: 'invite-sent',     label: 'Надіслано (очікує відповіді)' },
                          { value: 'invite-accepted', label: 'Прийнято батьками' },
                          { value: 'invite-declined', label: 'Відхилено батьками' },
                        ]}
                        selectedValues={filters.inviteStatuses || []}
                        onToggle={(val) => {
                          const cur = filters.inviteStatuses || [];
                          onFilterChange('inviteStatuses', cur.includes(val)
                            ? cur.filter(v => v !== val)
                            : [...cur, val]);
                        }}
                        onSelectAll={() => onFilterChange('inviteStatuses', ['invite-sent', 'invite-accepted', 'invite-declined'])}
                        onClearAll={() => onFilterChange('inviteStatuses', [])}
                        showTags={true}
                      />
                    </Col>

                    {/* Период запрошення */}
                    <Col md={4}>
                      <Form.Label className="form-label">Дата запрошення</Form.Label>
                      <DateRangePicker
                        start={filters.inviteDateFrom}
                        end={filters.inviteDateTo}
                        onChange={(start, end) => {
                          onFilterChange('inviteDateFrom', start);
                          onFilterChange('inviteDateTo', end);
                        }}
                      />
                    </Col>

                    {/* Садочок запрошення */}
                    <Col md={4}>
                      <MultiAutocompleteSelect
                        label="Садочок запрошення"
                        value={filters.inviteKindergartens || []}
                        onChange={(ids) => onFilterChange('inviteKindergartens', ids)}
                        options={inviteKindergartenOptions}
                        placeholder="Пошук садочка..."
                        loading={inviteKindergartenLoading}
                        hasMore={inviteKindergartenHasMore}
                        onSearch={onInviteKindergartenSearch}
                        onLoadMore={onInviteKindergartenLoadMore}
                        extraOptionsById={inviteKgById}
                        fieldsValue={['name']}
                      />
                    </Col>
                  </Row>

                  {/* Додаткові свічі про запрошення */}
                  <Row className="mt-2">
                    <Col>
                      <Form.Check
                        type="switch"
                        label="Було запрошення (будь-яке)"
                        checked={filters.hasInvite || false}
                        onChange={(e) => onFilterChange('hasInvite', e.target.checked)}
                        className="mb-2"
                      />
                      <Form.Check
                        type="switch"
                        label="Відхиляв запрошення (≥1)"
                        checked={filters.hasDeclinedInvite || false}
                        onChange={(e) => onFilterChange('hasDeclinedInvite', e.target.checked)}
                      />
                    </Col>
                  </Row>
                </Col>

                {/* Статусы */}
                <Col md={6} lg={4}>
                  <h6>Статус заявки</h6>
                  <CheckboxDropdown
                    label="Статуси"
                    options={[
                      { value: 'need-check', label: 'Садочки не обрані' },
                      { value: 'approved', label: 'Садочки обрано' },
                      { value: 'invite-sent', label: 'Надіслано запрошення' },
                      { value: 'invite-accepted', label: 'Прийнято запрошення' },
                      { value: 'invite-declined', label: 'Відхилено запрошення' },
                      { value: 'removed', label: 'Видалена' },
                      { value: 'child-in-kindergarten', label: 'Дитину оформлено' },
                    ]}
                    selectedValues={filters.statuses || []}
                    onToggle={(val) => {
                      const newVal = filters.statuses?.includes(val)
                        ? filters.statuses.filter(v => v !== val)
                        : [...(filters.statuses || []), val];
                      onFilterChange('statuses', newVal);
                    }}
                    onSelectAll={() => onFilterChange('statuses', ['need-check', 'approved', 'invite-sent', 'invite-accepted', 'invite-declined', 'removed', 'child-in-kindergarten'])}
                    onClearAll={() => onFilterChange('statuses', [])}
                    showTags={true}
                  />
                  <div className="mt-3">
                    <Form.Check
                      type="switch"
                      label="Вибрані садочки"
                      checked={filters.hasSelectedKindergartens || false}
                      onChange={(e) => onFilterChange('hasSelectedKindergartens', e.target.checked)}
                    />
                  </div>
                </Col>

                {/* Районы */}
                <Col md={6} lg={4}>
                  <h6>Обрані садочки</h6>
                  <CheckboxDropdown
                    label="Райони"
                    options={districtOptions}
                    selectedValues={filters.districts || []}
                    onToggle={(val) => {
                      const newVal = filters.districts?.includes(val)
                        ? filters.districts.filter(v => v !== val)
                        : [...(filters.districts || []), val];
                      onFilterChange('districts', newVal);
                    }}
                    onSelectAll={() => onFilterChange('districts', districtOptions.map(opt => opt.value))}
                    onClearAll={() => onFilterChange('districts', [])}
                    showTags={true}
                  />
                </Col>

                {/* Выбранные ЗДО */}
                <Col md={6} lg={4}>
                
                <h6>Обрані садочки</h6>
                  <div className="d-flex gap-1">
                          <div className="flex-grow-1">
                            <MultiAutocompleteSelect
                              label="Садочки"
                              value={filters.selectedKindergartens || []}
                              onChange={(ids) => onFilterChange('selectedKindergartens', ids)}
                              options={kindergartenOptions}
                              placeholder="Пошук садочка..."
                              loading={kindergartenLoading}
                              hasMore={kindergartenHasMore}
                              onSearch={onKindergartenSearch}
                              onLoadMore={onKindergartenLoadMore}
                              extraOptionsById={kgById}
                              fieldsValue={['name']}
                            />
                          </div>
                          
                        </div>
                        {selectedKindergartensList.length > 0 && (
                          <div className="selected-kindergartens-list-compact mt-1">
                            {selectedKindergartensList.map(kg => (
                              <span key={kg.id} className="selected-kg-tag-compact">
                                {kg.name.length > 30 ? kg.name.substring(0, 27) + '...' : kg.name}
                                <button
                                  type="button"
                                  className="remove-kg-btn-compact"
                                  onClick={() => onRemoveKindergarten(kg.id)}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                </Col>
              </Row>
            </Tab>

            {/* Вкладка КОЛОНКИ */}
            <Tab 
              eventKey="columns" 
              title={<span><FontAwesomeIcon icon={faColumns} className="me-1" /> Колонки</span>}
            >
              <Row>
                <Col md={12}>
                  <div className="mb-3 d-flex gap-2">
                    <Button size="sm" variant="outline-primary" onClick={onSelectAllColumns}>
                      Вибрати всі
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={onClearAllColumns}>
                      Скинути всі
                    </Button>
                  </div>
                  <div className="columns-grid">
                    {availableColumns.map(column => (
                      <Form.Check
                        key={column.key}
                        type="checkbox"
                        id={`col-${column.key}`}
                        label={column.label}
                        checked={visibleColumns.includes(column.key)}
                        onChange={() => onColumnToggle(column.key)}
                        className="mb-2"
                      />
                    ))}
                  </div>
                </Col>
              </Row>
            </Tab>
          </Tabs>

          {/* Кнопки действий */}
          <div className="mt-3 d-flex justify-content-end gap-2 border-top pt-3">
            <Button 
              variant="outline-danger" 
              size="sm"
              onClick={handleResetAll}
            >
              Скинути всі фільтри
            </Button>
            <Button variant="primary" onClick={handleApplyAndClose}>
              Застосувати
            </Button>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default AdvancedFiltersAndColumns;